import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutside } from "@hooks/useOutside";
import { Element, scroller } from "react-scroll/modules";
import { Check, SmallArrow } from "@assets/icons";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import useDeferredRenderEffect from "@hooks/useDifferredRenderEffects";
import styles from "./Dropdown.module.scss";

const Dropdown = ({
  onChange,
  classSelect,
  label,
  options,
  scrollID = "default",
  error,
  value,
  className,
  placeholder,
  writableDropdown,
  style = {},
  multiSelect = false,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [localOptions, setLocalOptions] = useState(options);

  useEffect(() => {
    if (value === "") setInputValue("");
  }, [value]);

  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const wrapperRef = useRef(null);
  useOutside(wrapperRef, setIsOpen);

  const scroll = (fieldName) => {
    scroller.scrollTo(`value-${fieldName}`, {
      duration: 300,
      smooth: true,
      containerId: `dropdown-${scrollID}`,
      offset: -5,
    });
  };

  useDeferredRenderEffect(() => {
    if (value && isOpen) scroll(value);
  }, [value, isOpen]);

  const onClick = (option) => {
    if (!option?.value) return;

    if (multiSelect) {
      let newValue = Array.isArray(value) ? [...value] : [];
      if (newValue.includes(option.value)) {
        newValue = newValue.filter((v) => v !== option.value);
      } else {
        newValue.push(option.value);
      }
      onChange(newValue);
    } else {
      setInputValue(option.label);
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const typedValue = e.target.value;
    setInputValue(typedValue);

    if (!typedValue) {
      setLocalOptions(options);
      return;
    }

    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(typedValue.toLowerCase()),
    );
    setLocalOptions(filteredOptions);
  };

  const selectedLabelText = useMemo(() => {
    if (multiSelect) {
      if (Array.isArray(value) && value.length > 0) {
        const selectedLabels = localOptions
          .filter((v) => value.includes(v.value))
          .map((v) => v.label);

        if (selectedLabels.length === 1) return selectedLabels[0];
        if (selectedLabels.length > 1)
          return `${selectedLabels[0]} (+${selectedLabels.length - 1})`;
      }
      return placeholder || "-";
    }

    if (value || value === 0) {
      return localOptions.find((v) => v.value === value)?.label || "";
    }

    return placeholder || "-";
  }, [multiSelect, value, localOptions, placeholder]);

  const viewLabel = useCallback(() => {
    const isSelected = multiSelect
      ? Array.isArray(value) && value.length > 0
      : value || value === 0;

    return (
      <span
        className={clsx(
          "block truncate whitespace-nowrap overflow-hidden text-ellipsis",
          isSelected ? "text-dawn-gray" : "text-gray-light",
        )}
        title={isSelected ? selectedLabelText : undefined}
      >
        {selectedLabelText}
      </span>
    );
  }, [multiSelect, value, selectedLabelText]);

  return (
    <div className={clsx("relative", className)} style={style}>
      {!!label && <p className="text-cloudy-gray pl-4 mb-3.5">{label}</p>}
      <div
        className={clsx(
          "w-full min-w-0 rounded border border-solid flex items-center justify-between px-4 shadow-redShadow cursor-pointer",
          classSelect,
          isOpen ? "pointer-events-none" : "",
          // Both classes set `border-color` — applying them together left
          // the outcome dependent on stylesheet order, which silently
          // favored "transparent" and hid the error border entirely. Only
          // one can apply at a time now.
          error ? "border-light-red" : "border-transparent",
          isDisabled ? "opacity-50 cursor-not-allowed" : "",
        )}
        onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
      >
        {writableDropdown ? (
          <input
            type="text"
            className="p-2 bg-silver-light text-dawn-gray w-full"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder || "Type or select an option"}
          />
        ) : (
          <div className="flex-1 min-w-0 py-2 pr-2">{viewLabel()}</div>
        )}
        <SmallArrow className="shrink-0 transform rotate-90 stroke-current text-dawn-gray" />
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={clsx(
              "absolute z-20 flex flex-1 flex-row w-full rounded-sm top-full shadow",
              styles.list,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, translateY: "0.625rem" }}
            exit={{ opacity: 0, translateY: "1.25rem" }}
          >
            <div
              className={clsx(
                "flex flex-col flex-1 bg-white p-1",
                styles.hideWithScrollbar,
              )}
              ref={wrapperRef}
              id={`dropdown-${scrollID}`}
            >
              {localOptions.map((item) => {
                const isSelected = multiSelect
                  ? Array.isArray(value) && value.includes(item.value)
                  : value === item.value;
                return (
                  <Element
                    key={item.value}
                    name={`value-${item.value}`}
                    className={clsx(
                      "px-2 text-dawn-gray relative rounded-md py-2 cursor-pointer flex flex-row justify-between items-center",
                      isSelected ? "bg-pampas font-medium-poppins" : "",
                      multiSelect ? "m-2 py-2" : "",
                    )}
                    onClick={() => !isDisabled && onClick(item)}
                    title={item.label}
                  >
                    <span className="flex-1 min-w-0 truncate">
                      {item.label}
                    </span>
                    {isSelected ? <Check className="mb-1 shrink-0" /> : null}
                  </Element>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dropdown;
